import {
  AsYouType,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useState,
  type FC,
} from 'react';
import {
  AccessibilityInfo,
  Platform,
  Pressable,
  TextInput as RNTextInput,
  View,
} from 'react-native';

import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { getIosBoldTextAdjustedFontWeightStyle } from '@/components/ui/text/ios-bold-text-font-weight';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/theme';

import { Stack } from '@/components/layout';
import { StackLayoutProps } from '@/components/layout/stack/types';
import { createStyles, useScaling } from '@/hooks';
import { TypographyToken } from '@/theme/typography';
import type {
  TextInputContextValue,
  TextInputFieldProps,
  TextInputFlagProps,
  TextInputHelperTextProps,
  TextInputIconProps,
  TextInputLabelProps,
  TextInputPasswordToggleProps,
  TextInputRootProps,
} from './types';

const TextInputContext = createContext<TextInputContextValue | null>(null);

const useTextInputContext = () => {
  const ctx = useContext(TextInputContext);
  if (!ctx) {
    throw new Error('TextInput subcomponents must be used within a <TextInput.Root> component.');
  }
  return ctx;
};

const TextInputLabel: FC<TextInputLabelProps> = ({ children, style, ...rest }) => {
  const styles = useStyles();
  return (
    <Text variant="Font Inter/Subtitle/Regular 11px" style={[styles.label, style]} {...rest}>
      {children}
    </Text>
  );
};

const TextInputHelperText: FC<TextInputHelperTextProps> = ({ children, style, ...rest }) => {
  const { status } = useTextInputContext();
  const styles = useStyles();
  const color = status === 'error' ? colors.Error : colors['Labels/Secondary'];

  return (
    <Text
      variant="Font Inter/Subtitle/Regular 11px"
      style={[styles.helperText, { color }, style]}
      {...rest}>
      {children}
    </Text>
  );
};

const ContentContainer: FC<StackLayoutProps> = ({ children, style, ...rest }) => {
  const { status, disabled } = useTextInputContext();
  const styles = useStyles();
  const borderColor = status === 'error' ? colors.Error : colors['5'];
  const backgroundColor = disabled ? colors['1'] : colors['Backgrounds/Primary'];
  const { scale, isTablet } = useScaling();

  return (
    <Stack
      direction="row"
      align="center"
      borderWidth={scale(1)}
      borderRadius={scale(4)}
      style={[
        isTablet ? styles.containerIpad : styles.containerPhone,
        {
          borderColor,
          backgroundColor,
        },
        style,
      ]}
      {...rest}>
      {children}
    </Stack>
  );
};

const MemoizedHelperText = memo(TextInputHelperText);
const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

const toFlagEmoji = (countryCode?: CountryCode) => {
  if (!countryCode) {
    return '🏳️';
  }

  return countryCode
    .toUpperCase()
    .split('')
    .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
};

const TextInputRoot: FC<TextInputRootProps> = ({
  children,
  status = 'default',
  disabled = false,
  style,
  value,
  onChangeText,
  onSubmitEditing,
  onBlur,
  defaultSecureTextEntry,
  secureTextEntry: secureTextEntryProp,
  onSecureTextEntryChange,
  phoneNumber = false,
  phoneDefaultCountry = DEFAULT_PHONE_COUNTRY,
  onCountryCodeChange,
  onFocus,
}) => {
  const isControlledSecure =
    typeof secureTextEntryProp === 'boolean' && typeof onSecureTextEntryChange === 'function';

  const [secureTextEntryInternal, setSecureTextEntryInternal] = useState(
    defaultSecureTextEntry ?? false,
  );

  const usePasswordToggle = defaultSecureTextEntry === true || isControlledSecure;
  const secureTextEntryResolved = isControlledSecure
    ? secureTextEntryProp
    : secureTextEntryInternal;

  const setSecureTextEntry = useCallback(
    (next: boolean) => {
      if (isControlledSecure) {
        onSecureTextEntryChange(next);
        return;
      }
      setSecureTextEntryInternal(next);
    },
    [isControlledSecure, onSecureTextEntryChange],
  );

  const stringValue = typeof value === 'string' ? value : '';
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode | undefined>(() => {
    if (!phoneNumber) return undefined;

    // On the initial render only: fall back to `phoneDefaultCountry` when we can't parse.
    const parsed = parsePhoneNumberFromString(stringValue, phoneDefaultCountry);
    return parsed?.country ?? phoneDefaultCountry;
  });

  const handleCountryCodeChange = useEffectEvent((countryCode: CountryCode | undefined) => {
    onCountryCodeChange?.(countryCode);
  });

  useEffect(() => {
    if (phoneNumber) {
      handleCountryCodeChange(phoneCountryCode);
    }
  }, [phoneCountryCode, phoneNumber]);

  useEffect(() => {
    if (!phoneNumber) {
      setPhoneCountryCode(undefined);
      return;
    }

    const startWithPlus = stringValue.startsWith('+');
    if (startWithPlus) {
      const parsed = parsePhoneNumberFromString(stringValue, phoneDefaultCountry);
      const nextCountry = parsed?.country;

      // Only update when parsing succeeded; otherwise keep the previously parsed value.
      if (nextCountry) {
        setPhoneCountryCode(nextCountry);
      }
      return;
    }
  }, [phoneNumber, phoneDefaultCountry, stringValue]);

  const handleChangeText = useCallback<NonNullable<TextInputRootProps['onChangeText']>>(
    nextValue => {
      if (!onChangeText) {
        return;
      }

      if (!phoneNumber) {
        onChangeText(nextValue);
        return;
      }

      const isDeleting = nextValue.length < stringValue.length;

      if (isDeleting) {
        const possiblePhone = isPossiblePhoneNumber(nextValue, phoneCountryCode);
        if (possiblePhone) {
          const _phoneNumber = parsePhoneNumberFromString(nextValue, phoneCountryCode);
          const hasLeadingPlus = nextValue.startsWith('+');
          onChangeText(
            hasLeadingPlus
              ? (_phoneNumber?.formatInternational() ?? '')
              : (_phoneNumber?.formatNational() ?? ''),
            phoneCountryCode,
          );
          return;
        }
        onChangeText(nextValue);
        return;
      }

      const formatter = new AsYouType(phoneCountryCode);
      const formattedPhone = formatter.input(nextValue);
      onChangeText(formattedPhone, phoneCountryCode);
    },
    [onChangeText, phoneCountryCode, phoneNumber, stringValue],
  );

  return (
    <TextInputContext.Provider
      value={{
        status,
        disabled,
        phoneNumber,
        phoneDefaultCountry,
        phoneCountryCode,
        value,
        onChangeText: handleChangeText,
        onBlur,
        onSubmitEditing,
        onFocus,
        ...(usePasswordToggle && {
          secureTextEntry: secureTextEntryResolved,
          setSecureTextEntry,
        }),
      }}>
      <Stack direction="column" width="100%" justify="center" style={style}>
        {children}
      </Stack>
    </TextInputContext.Provider>
  );
};

type RNTextInputInstance = React.ElementRef<typeof RNTextInput>;

function assignTextInputRef(
  ref: React.Ref<RNTextInputInstance> | undefined,
  node: RNTextInputInstance | null,
) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  try {
    (ref as React.MutableRefObject<RNTextInputInstance | null>).current = node;
  } catch {
    // ignore invalid ref objects
  }
}

const TextInputField: FC<TextInputFieldProps> = ({
  variant,
  style,
  editable = true,
  secureTextEntry: secureTextEntryProp,
  multiline,
  numberOfLines,
  ...rest
}) => {
  const { ref: refFromProps, ...fieldRest } = rest as typeof rest & {
    ref?: React.Ref<RNTextInputInstance>;
  };
  const _multiline = multiline ?? false;
  const _numberOfLines = numberOfLines ?? (multiline ? undefined : 1);
  const {
    disabled,
    phoneNumber,
    value,
    onChangeText,
    onSubmitEditing,
    secureTextEntry: secureTextEntryContext,
    onBlur,
    onFocus,
  } = useTextInputContext();
  const styles = useStyles();
  const isEditable = !disabled && editable;
  const secureTextEntry =
    secureTextEntryProp !== undefined ? secureTextEntryProp : (secureTextEntryContext ?? false);

  const { scale, isTablet } = useScaling();
  const [iosBoldTextEnabled, setIosBoldTextEnabled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  /**
   * Android (Fabric): static `selection` / setNativeProps often does not apply for pre-filled
   * single-line inputs. RN issue #44171 — drive `selection` from state and clear it on focus.
   */
  const [androidSelection, setAndroidSelection] = useState<
    { start: number; end?: number } | undefined
  >(undefined);

  const setInputRef = useCallback(
    (node: RNTextInputInstance | null) => {
      assignTextInputRef(refFromProps, node);
    },
    [refFromProps],
  );

  useLayoutEffect(() => {
    if (Platform.OS !== 'android') return;
    if (isFocused || _multiline) {
      setAndroidSelection(undefined);
      return;
    }
    if (typeof value !== 'string' || value.length === 0) {
      setAndroidSelection(undefined);
      return;
    }
    // Clear then pin so Fabric reapplies scroll/caret after value updates (e.g. form.reset).
    setAndroidSelection(undefined);
    queueMicrotask(() => {
      setAndroidSelection({ start: 0, end: 0 });
    });
    const pin = () => setAndroidSelection({ start: 0, end: 0 });
    const t0 = setTimeout(pin, 0);
    const t1 = setTimeout(pin, 32);
    const t2 = setTimeout(pin, 100);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [value, isFocused, _multiline]);

  const typographyKey =
    variant ??
    (isTablet
      ? ('Font Inter/Body/Regular 16px' as TypographyToken)
      : ('Font Inter/Subtitle/Regular 12px' as TypographyToken));

  const typo = theme.typography[typographyKey];
  const scaledTypo = {
    ...typo,
    fontSize: scale(typo.fontSize),
    lineHeight: Platform.OS !== 'ios' ? scale(typo.lineHeight) : undefined,
  };

  const iosBoldTextInputStyle = useMemo(
    () =>
      iosBoldTextEnabled ? getIosBoldTextAdjustedFontWeightStyle(scaledTypo.fontWeight) : undefined,
    [iosBoldTextEnabled, scaledTypo.fontWeight],
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setIosBoldTextEnabled(false);
      return;
    }

    const sync = (enabled: boolean) => {
      setIosBoldTextEnabled(enabled);
    };

    let cancelled = false;
    (async () => {
      const enabled = await AccessibilityInfo.isBoldTextEnabled();
      if (!cancelled) sync(enabled);
    })();

    const subscription = AccessibilityInfo.addEventListener('boldTextChanged', sync);
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  return (
    <RNTextInput
      ref={setInputRef}
      allowFontScaling={false}
      value={value}
      multiline={_multiline}
      numberOfLines={_numberOfLines}
      {...fieldRest}
      keyboardType={
        phoneNumber ? (fieldRest.keyboardType ?? 'name-phone-pad') : fieldRest.keyboardType
      }
      editable={isEditable}
      secureTextEntry={secureTextEntry}
      style={[scaledTypo, iosBoldTextInputStyle, styles.input, styles.inputText, style]}
      placeholderTextColor={colors[5]}
      onChangeText={onChangeText}
      onSubmitEditing={onSubmitEditing}
      // When long text is pre-filled (controlled input), RN often keeps the caret at the end and
      // horizontally scrolls so the beginning isn't visible. For edit forms we want to show
      // the beginning by default, while still letting users place the caret normally on focus.
      // iOS: static `selection` when blurred works. Android (Fabric): controlled `androidSelection`.
      selection={
        Platform.OS === 'android'
          ? androidSelection
          : Platform.OS === 'ios' &&
              !isFocused &&
              !_multiline &&
              typeof value === 'string' &&
              value.length > 0
            ? { start: 0, end: 0 }
            : undefined
      }
      onBlur={e => {
        setIsFocused(false);
        onBlur?.(e);
      }}
      onFocus={e => {
        setIsFocused(true);
        if (Platform.OS === 'android') {
          setAndroidSelection(undefined);
        }
        onFocus?.(e);
      }}
    />
  );
};

const TextInputIcon: FC<TextInputIconProps> = ({
  position = 'left',
  size,
  color,
  styleWrapper,
  ...iconProps
}) => {
  const { disabled } = useTextInputContext();
  const styles = useStyles();
  const { scale } = useScaling();
  const baseSize = size ?? scale(24);
  const resolvedColor = disabled ? colors['5'] : (color ?? colors['Blue 2']);

  return (
    <View
      style={[
        styles.iconWrapper,
        position === 'left' ? styles.iconLeft : styles.iconRight,
        styleWrapper,
      ]}>
      <SvgIcon size={baseSize} color={resolvedColor} {...iconProps} />
    </View>
  );
};

const TextInputFlag: FC<TextInputFlagProps> = ({ position = 'left', styleWrapper }) => {
  const styles = useStyles();
  const { phoneNumber, phoneCountryCode } = useTextInputContext();

  if (!phoneNumber) {
    return null;
  }

  return (
    <View
      style={[
        styles.flagWrapper,
        position === 'left' ? styles.iconLeft : styles.iconRight,
        styleWrapper,
      ]}>
      <Text variant="Font Inter/Body/Regular 16px">{toFlagEmoji(phoneCountryCode)}</Text>
    </View>
  );
};

const TextInputPasswordToggle: FC<TextInputPasswordToggleProps> = ({
  size,
  color,
  styleWrapper,
}) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const { disabled, secureTextEntry, setSecureTextEntry } = useTextInputContext();
  const baseSize = size ?? scale(24);
  const resolvedColor = disabled ? colors['5'] : (color ?? colors['Blue 2']);

  if (setSecureTextEntry == null) {
    return null;
  }

  const isHidden = secureTextEntry === true;

  return (
    <Pressable
      onPress={() => setSecureTextEntry(!isHidden)}
      style={({ pressed }) => [
        styles.iconWrapper,
        styles.iconRight,
        styles.passwordToggle,
        styleWrapper,
        pressed && styles.passwordTogglePressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={isHidden ? 'Show password' : 'Hide password'}
      accessibilityState={{ expanded: !isHidden }}>
      <SvgIcon name={isHidden ? 'IcEyeOff' : 'IcEye'} size={baseSize} color={resolvedColor} />
    </Pressable>
  );
};

const useStyles = createStyles(({ scale }) => ({
  wrapper: {
    width: '100%',
  },
  containerPhone: {
    paddingLeft: scale(4),
    paddingRight: scale(8),
    height: scale(44),
  },
  containerIpad: {
    paddingHorizontal: scale(12),
    height: scale(48),
  },
  input: {
    flex: 1,
    margin: 0,
  },
  inputText: {
    color: colors['Labels/Primary'],
    textAlign: Platform.OS === 'ios' ? undefined : 'left',
    paddingVertical: 0,
  },
  iconLeft: {
    marginRight: scale(12),
  },
  iconRight: {
    marginLeft: scale(12),
  },
  label: {
    color: colors['Labels/Primary'],
  },
  helperText: {
    marginTop: scale(6),
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(24),
    height: scale(24),
  },
  flagWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: scale(24),
  },
  passwordToggle: {
    marginLeft: scale(4),
  },
  passwordTogglePressed: {
    opacity: 0.7,
  },
}));

const MemoizedRoot = memo(TextInputRoot);
const MemoizedField = memo(TextInputField);
const MemoizedLabel = memo(TextInputLabel);
const MemoizedIcon = memo(TextInputIcon);
const MemoizedFlag = memo(TextInputFlag);
const MemoizedContentContainer = memo(ContentContainer);
const MemoizedPasswordToggle = memo(TextInputPasswordToggle);

export const TextInput = Object.assign(MemoizedRoot, {
  Field: MemoizedField,
  Label: MemoizedLabel,
  HelperText: MemoizedHelperText,
  Icon: MemoizedIcon,
  Flag: MemoizedFlag,
  Content: MemoizedContentContainer,
  PasswordToggle: MemoizedPasswordToggle,
});

export default TextInput;
